export async function requestNativeCachePurge(
  fetchCache = window.fetch,
  storage = window.localStorage,
  secure = window.location.protocol === "https:",
) {
  const marker = "easybar-retired-font-cache-2026-10-13";
  if (!secure || storage.getItem(marker) === "done") return;
  const response = await fetchCache("/.well-known/easybar-retired-font-cache-2026-10-13", {
    cache: "no-store", credentials: "same-origin",
  });
  if (response.status === 204 && response.headers.get("Clear-Site-Data")?.includes('"cache"')) {
    storage.setItem(marker, "done");
  }
}

export async function removeLegacyAppWorkers() {
  if (!("serviceWorker" in navigator)) return;
  for (const registration of await navigator.serviceWorker.getRegistrations()) {
    const worker = registration.active || registration.waiting || registration.installing;
    if (!worker) continue;
    const url = new URL(worker.scriptURL);
    if (url.origin === window.location.origin &&
        ["/service-worker.js", "/content/service-worker.js", "/sw.js", "/content/sw.js"].includes(url.pathname)) {
      await registration.unregister();
    }
  }
}

// Remove a retired unsafe asset from this origin's browser caches.
// No worker is registered by this version; retire only known legacy app workers.
export async function removeUnsafeFontCache() {
  await removeLegacyAppWorkers().catch(() => undefined);
  await requestNativeCachePurge().catch(() => undefined);
  if (!("caches" in window)) return;
  const hashes = new Set([
    "13e9a3c41e038bf9d8fcb0831305819819e4f7f4452bc20a04b9bf2756ee22e8",
    "02d4c0a943d6489b5c527c2dc449835add39f1c010801c6ac91886b5c6dd3e61",
  ]);
  try {
    for (const name of await caches.keys()) {
      const cache = await caches.open(name);
      for (const request of await cache.keys()) {
        if (new URL(request.url).pathname.split("/").pop()?.toLowerCase() === "fa-solid-400.woff2") {
          await cache.delete(request);
          continue;
        }
        const response = await cache.match(request);
        if (!response || response.type === "opaque" || !window.crypto?.subtle) continue;
        const length = response.headers.get("Content-Length");
        if (length && ![5102, 5103].includes(Number(length))) continue;
        const data = await response.arrayBuffer();
        if (![5102, 5103].includes(data.byteLength)) continue;
        const digest = await crypto.subtle.digest("SHA-256", data);
        const hex = Array.from(new Uint8Array(digest)).map(value => value.toString(16).padStart(2, "0")).join("");
        if (hashes.has(hex)) await cache.delete(request);
      }
    }
  } catch {
    // Private browsing may disable CacheStorage; the app still uses clean assets.
    console.warn("Unable to inspect retired font cache");
  }
}
